import { ErrorOutlined as ErrorOutlineIcon } from '@mui/icons-material';
import { alpha, Box, Stack, Typography, useTheme } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { AddItemFormData } from '../../hooks/use-add-item-form';
import { useExtractedData } from '../extracted-data-context';

type RequiredKey = 'productName' | 'batchNumber' | 'expiryDate';

function useMissingRequiredFields(): RequiredKey[] {
  const { watch } = useFormContext<AddItemFormData>();
  const productName = watch('productName');
  const batchNumber = watch('batchNumber');
  const expiryDate = watch('expiryDate');

  const missing: RequiredKey[] = [];
  if (!productName?.trim()) {
    missing.push('productName');
  }
  if (!batchNumber?.trim()) {
    missing.push('batchNumber');
  }
  if (!expiryDate?.trim()) {
    missing.push('expiryDate');
  }
  return missing;
}

export function useShouldHighlightAiGaps(): boolean {
  const { extractionCompletedAt } = useExtractedData();
  return extractionCompletedAt !== null;
}

export function AiGapBanner() {
  const { t } = useTranslation('goodsReceiving');
  const theme = useTheme();
  const shouldHighlight = useShouldHighlightAiGaps();
  const missing = useMissingRequiredFields();

  if (!shouldHighlight || missing.length === 0) {
    return null;
  }

  const accent = theme.palette.warning.main;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: alpha(accent, 0.4),
        bgcolor: alpha(accent, 0.08),
        px: 1.75,
        py: 1.5,
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        sx={{
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: 32,
            height: 32,
            borderRadius: 2,
            bgcolor: alpha(accent, 0.2),
            color: accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& .MuiSvgIcon-root': { fontSize: '1.15rem' },
          }}
          aria-hidden="true"
        >
          <ErrorOutlineIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.88rem', lineHeight: 1.3 }}
          >
            {t('aiGap.bannerTitle', { count: missing.length })}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'text.secondary',
              mt: 0.25,
              fontSize: '0.76rem',
              lineHeight: 1.4,
            }}
          >
            {t('aiGap.bannerHelper')}
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              flexWrap: 'wrap',
              mt: 0.75,
              gap: 0.5,
            }}
          >
            {missing.map((field) => (
              <Box
                key={field}
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: accent,
                  color: accent,
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                }}
              >
                {t(`aiGap.fields.${field}`)}
              </Box>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
